/**
 * VendorCard Component
 * Vendor preview card for listings
 * BP-08-04: Marketplace Frontend (Family)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  CheckCircle2, 
  Building2,
  ArrowRight 
} from 'lucide-react';
import { StarRating } from './StarRating';
import type { VendorCardProps } from '@/features/marketplace/types/marketplaceTypes';

export const VendorCard: React.FC<VendorCardProps> = ({
  vendor,
  variant = 'default',
  showCategories = true,
  showLocation = true,
}) => {
  const {
    slug,
    name,
    description,
    logoUrl,
    isVerified,
    avgRating,
    reviewCount,
    serviceAreaNational,
    serviceAreaStates,
    categories,
    yearsInBusiness,
  } = vendor;
  
  const locationText = serviceAreaNational
    ? 'Nationwide'
    : serviceAreaStates.length > 0
      ? serviceAreaStates.slice(0, 3).join(', ') + (serviceAreaStates.length > 3 ? '...' : '')
      : null;
  
  if (variant === 'compact') {
    return (
      <Link
        to={`/marketplace/vendors/${slug}`}
        className="
          flex items-center gap-4 p-4
          bg-white rounded-lg border border-warm-gray/10
          hover:border-sage/30 hover:shadow-soft
          transition-all duration-200
        "
      >
        {/* Logo */}
        <div className="
          w-14 h-14 rounded-lg bg-cream-dark
          flex items-center justify-center flex-shrink-0
          overflow-hidden
        ">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Building2 className="w-6 h-6 text-warm-gray" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-navy truncate">{name}</h3>
            {isVerified && (
              <CheckCircle2 className="w-4 h-4 text-sage flex-shrink-0" />
            )}
          </div>
          
          {avgRating !== null && (
            <div className="flex items-center gap-1 mt-1">
              <StarRating rating={avgRating} size="sm" />
              <span className="text-xs text-warm-gray">({reviewCount})</span>
            </div>
          )}
        </div>
        
        <ArrowRight className="w-5 h-5 text-warm-gray" />
      </Link>
    );
  }
  
  return (
    <Link
      to={`/marketplace/vendors/${slug}`}
      className="
        group block bg-white rounded-xl
        border border-warm-gray/10
        overflow-hidden
        hover:border-sage/30 hover:shadow-medium
        transition-all duration-300
      "
    >
      {/* Header/Logo */}
      <div className="relative h-32 bg-gradient-to-br from-cream to-cream-dark">
        {vendor.bannerUrl ? (
          <img
            src={vendor.bannerUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : null}
        
        {/* Logo overlay */}
        <div className="
          absolute -bottom-8 left-4
          w-16 h-16 rounded-xl bg-white
          border-4 border-white shadow-soft
          flex items-center justify-center
          overflow-hidden
        ">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Building2 className="w-8 h-8 text-warm-gray" />
          )}
        </div>
        
        {/* Verified badge */}
        {isVerified && (
          <div className="
            absolute top-3 right-3
            flex items-center gap-1.5
            bg-white/90 backdrop-blur-sm
            px-2.5 py-1 rounded-full
            text-xs font-medium text-sage-dark
          ">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Verified
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="pt-10 px-4 pb-4">
        {/* Name and Rating */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="
            font-display font-semibold text-lg text-navy
            group-hover:text-sage-dark transition-colors
          ">
            {name}
          </h3>
        </div>
        
        {/* Rating */}
        {avgRating !== null && (
          <div className="flex items-center gap-2 mb-3">
            <StarRating rating={avgRating} size="sm" />
            <span className="text-sm text-warm-gray">
              {avgRating.toFixed(1)} ({reviewCount} reviews)
            </span>
          </div>
        )}
        
        {/* Description */}
        {description && (
          <p className="text-sm text-warm-gray line-clamp-2 mb-3">
            {description}
          </p>
        )}
        
        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-3 text-sm text-warm-gray">
          {showLocation && locationText && (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{locationText}</span>
            </div>
          )}
          
          {yearsInBusiness && (
            <span>{yearsInBusiness}+ years</span>
          )}
        </div>
        
        {/* Categories */}
        {showCategories && categories && categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {categories.slice(0, 3).map((category) => (
              <span
                key={category.id}
                className="
                  px-2 py-0.5 rounded-full
                  bg-cream text-xs font-medium text-navy-light
                "
              >
                {category.name}
              </span>
            ))}
            {categories.length > 3 && (
              <span className="
                px-2 py-0.5 rounded-full
                bg-cream-dark text-xs font-medium text-warm-gray
              ">
                +{categories.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default VendorCard;

